import { useCallback, useEffect, useState } from 'preact/hooks';
import { api } from './api/client';
import type { UserOut } from './api/types';
import { Layout } from './components/Layout';
import { Spinner } from './components/ui';
import { t } from './i18n';
import { useLang } from './lib/hooks';
import { navigate, routeHref, useRoute, type Route } from './router';
import { AnkiPage } from './pages/AnkiPage';
import { LoginPage } from './pages/LoginPage';
import { SettingsPage } from './pages/SettingsPage';
import { VocabPage } from './pages/VocabPage';

type SessionState = 'checking' | 'anonymous' | 'authenticated';

/** A wedged or unreachable server must not leave the splash on screen forever. */
const BOOT_TIMEOUT_MS = 8000;

/** Redirect during an effect, never during render (a synchronous hash change in
 * render would re-enter the router and loop). */
function Redirect({ to }: { to: string }) {
  useEffect(() => {
    navigate(to, { replace: true });
  }, [to]);
  return <Spinner />;
}

function RouteView({ route }: { route: Route }) {
  switch (route.name) {
    case 'vocab':
      return <VocabPage />;
    case 'settings':
      return <SettingsPage />;
    case 'anki':
      return <AnkiPage />;
    case 'notFound':
      return (
        <div class="page">
          <h1>{t('common.notFound')}</h1>
          <p class="muted">{t('common.notFoundHint')}</p>
          <a class="btn" href={routeHref('vocab')}>
            {t('nav.vocab')}
          </a>
        </div>
      );
    default:
      return null;
  }
}

export function App() {
  // Language subscription at the root keeps every page in sync with the toggle.
  useLang();
  const route = useRoute();
  const [session, setSession] = useState<SessionState>('checking');
  const [user, setUser] = useState<UserOut | null>(null);
  const [bootOffline, setBootOffline] = useState(false);
  const [bootAttempt, setBootAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), BOOT_TIMEOUT_MS);

    void api
      .restore({ signal: controller.signal })
      .then((restored) => {
        if (cancelled) return;
        setBootOffline(false);
        if (restored) {
          setUser(api.loadedUser);
          setSession('authenticated');
        } else {
          setSession('anonymous');
        }
      })
      .catch(() => {
        // Timeout or transport failure: show the login form with an explicit
        // "server unreachable" notice and a retry, never an endless spinner.
        if (cancelled) return;
        setBootOffline(true);
        setSession('anonymous');
      })
      .finally(() => clearTimeout(timer));

    const unsubscribe = api.onAuthLost(() => {
      setUser(null);
      setSession('anonymous');
      navigate(routeHref('login'), { replace: true });
    });

    return () => {
      cancelled = true;
      clearTimeout(timer);
      unsubscribe();
    };
  }, [bootAttempt]);

  const handleAuthenticated = useCallback(() => {
    void api
      .me()
      .then((value) => setUser(value))
      .catch(() => setUser(api.loadedUser))
      .finally(() => {
        setSession('authenticated');
        navigate(routeHref('vocab'), { replace: true });
      });
  }, []);

  const handleLogout = useCallback(() => {
    void api.logout();
    setUser(null);
    setSession('anonymous');
  }, []);

  if (session === 'checking') {
    return (
      <div class="splash">
        <Spinner label={t('health.checking')} />
      </div>
    );
  }

  if (session === 'anonymous') {
    if (route.name !== 'login') return <Redirect to={routeHref('login')} />;
    return (
      <LoginPage
        onAuthenticated={handleAuthenticated}
        offline={bootOffline}
        onRetry={() => {
          setSession('checking');
          setBootAttempt((value) => value + 1);
        }}
      />
    );
  }

  if (route.name === 'login') return <Redirect to={routeHref('vocab')} />;

  return (
    <Layout route={route.name} email={user?.email ?? null} onLogout={handleLogout}>
      <RouteView route={route} />
    </Layout>
  );
}
